import React, { useState, useEffect } from "react";
import { db } from "../../firebase/config";
import { collection, query, onSnapshot, getDocs, where, orderBy } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import Header from "../../components/Header";

const ROLES = {
  gestion_general: 'Gestión General',
  recepcion: 'Recepción',
  medico: 'Médico',
  administracion: 'Administración',
};

export default function SuperAdminUsuarios() {
  const { theme } = useTheme();
  const [clinicas, setClinicas] = useState([]);
  const [usuariosPorClinica, setUsuariosPorClinica] = useState({});
  const [expandida, setExpandida] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');

  const inputStyle = { padding: '8px 12px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, fontFamily: "'Montserrat', sans-serif" };

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'clinicas'), orderBy('nombre')), snap => {
      setClinicas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  async function cargarUsuarios(clinicaId) {
    if (usuariosPorClinica[clinicaId]) return;
    const snap = await getDocs(query(collection(db, 'usuarios'), where('clinicaId', '==', clinicaId)));
    setUsuariosPorClinica(prev => ({ ...prev, [clinicaId]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
  }

  async function toggleExpandida(clinica) {
    if (expandida === clinica.id) { setExpandida(null); return; }
    setExpandida(clinica.id);
    await cargarUsuarios(clinica.id);
  }

  const clinicasFiltradas = clinicas.filter(c =>
    !busqueda || c.nombre?.toLowerCase().includes(busqueda.toLowerCase()) || c.email?.toLowerCase().includes(busqueda.toLowerCase())
  );

  const totalUsuarios = Object.values(usuariosPorClinica).reduce((a, u) => a + u.length, 0);

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Usuarios" backLabel="Super Admin" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg, display: 'flex', flexDirection: 'column', gap: '16px' }}>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
          {[
            { label: 'Total clínicas', valor: clinicas.length, color: theme.accent, bg: theme.infoBg },
            { label: 'Usuarios cargados', valor: totalUsuarios, color: theme.success, bg: theme.successBg },
            { label: 'Clínicas activas', valor: clinicas.filter(c => c.estado === 'activa').length, color: theme.success, bg: theme.successBg },
            { label: 'En trial', valor: clinicas.filter(c => c.estado === 'trial').length, color: theme.warning, bg: theme.warningBg },
          ].map(item => (
            <div key={item.label} style={{ background: item.bg, padding: '14px 16px', borderRadius: '10px' }}>
              <div style={{ fontSize: '11px', color: item.color, marginBottom: '4px', fontWeight: '500' }}>{item.label}</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: item.color }}>{item.valor}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar clínica..." style={{ ...inputStyle, minWidth: '260px' }} />
          <span style={{ fontSize: '12px', color: theme.secondary }}>{clinicasFiltradas.length} clínicas</span>
        </div>

        {loading
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px' }}>Cargando...</div>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {clinicasFiltradas.map(c => {
                const usuarios = usuariosPorClinica[c.id] || [];
                const expandido = expandida === c.id;
                const estadoColor = { activa: theme.success, trial: theme.accent, suspendida: theme.error, pendiente_activacion: theme.warning, cancelada: theme.secondary };
                return (
                  <div key={c.id} style={{ background: theme.bgCard, borderRadius: '10px', border: `0.5px solid ${theme.border}`, overflow: 'hidden' }}>
                    <div onClick={() => toggleExpandida(c)} style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: theme.infoBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', color: theme.accent }}>{c.nombre?.[0]}</div>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: theme.primary }}>{c.nombre}</div>
                          <div style={{ fontSize: '12px', color: theme.secondary }}>{c.email} · Plan {c.plan || '—'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {expandido && usuarios.length > 0 && (
                          <span style={{ fontSize: '11px', color: theme.secondary }}>{usuarios.length} usuarios</span>
                        )}
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: `${estadoColor[c.estado] || theme.secondary}20`, color: estadoColor[c.estado] || theme.secondary, fontWeight: '500' }}>
                          {c.estado?.replace('_', ' ')}
                        </span>
                        <span style={{ color: theme.secondary, fontSize: '16px' }}>{expandido ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {expandido && (
                      <div style={{ padding: '0 18px 16px', borderTop: `0.5px solid ${theme.border}` }}>
                        {usuarios.length === 0
                          ? <div style={{ padding: '16px', textAlign: 'center', color: theme.secondary, fontSize: '13px' }}>No hay usuarios registrados</div>
                          : <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
                              {usuarios.map(u => (
                                <div key={u.id} style={{ background: theme.bg, borderRadius: '8px', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: theme.infoBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: theme.accent }}>{u.email?.[0]?.toUpperCase()}</div>
                                    <div>
                                      <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{u.nombre || u.email}</div>
                                      <div style={{ fontSize: '11px', color: theme.secondary }}>{u.email} · {ROLES[u.rol] || u.rol}</div>
                                    </div>
                                  </div>
                                  <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: u.activo ? theme.successBg : theme.errorBg, color: u.activo ? theme.success : theme.error, fontWeight: '500' }}>
                                    {u.activo ? 'Activo' : 'Inactivo'}
                                  </span>
                                </div>
                              ))}
                            </div>
                        }
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
        }
      </div>
    </div>
  );
}